import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Divider, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { PhoneIcon } from '@sharedCR/coreDesignSystem/icons';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { MarkedAppFormsTreeFieldId } from 'app/services/autoLockForms/autoLockForms.fieldIds';
import ClickablePhone from 'app/shared/components/ClickablePhone';
import Accordion from 'app/shared/components/common/Accordion';
import AudioPlayer from 'app/shared/components/common/AudioPlayer';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropType from 'prop-types';

import { getPersonFullName } from 'utils/loanApplication';

import { showMessageAction } from 'store/global/actions';
import { updateCallOutcomeAction } from 'store/loanApplications/actions';
import {
  selectContactsWithBorrowers,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectFormHiddenPage } from 'store/loanApplications/selectors/lockedFields.selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';
import { selectUserById } from 'store/users/selectors';

import { callOutcomeOptions, CallTypes } from '../../../Activities.constants';
import { getStyles } from '../../../Activities.styles';
import { ActivitiesTestIds } from '../../../Activities.testIds';
import {
  formatActivityDateTime,
  getFullNameByNumber,
  normalizeNumber,
} from '../../../Activities.utils';
import HighlightText from '../../../HighlightText';

import CallNotes from './CallNotes';
import CallTranscription from './CallTranscription';

const defaultObject = {};

const CallActivityItem = ({ activity = defaultObject, isCompanyUsersLoading }) => {
  const { applicationId } = useParams();

  const theme = useTheme();
  const classes = getStyles(theme);
  const dispatch = useDispatch();

  const contacts = useSelector(selectContactsWithBorrowers);
  const highlightText = useSelector(getDebouncedSearchValue);

  const {
    phoneNumberFrom = '',
    phoneNumberTo = '',
    recordingUrl,
    callTranscription,
    dateTime,
    callHistoryId,
    callNotes,
    callDisposition,
    callType,
    employeeUserId,
  } = activity;

  const callUser = useSelector(selectUserById(employeeUserId));
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ACTIVITIES_CALLS));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.ACTIVITIES_CALLS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ACTIVITIES_CALLS));
  const isPageHidden = useSelector(selectFormHiddenPage(FormNames.ACTIVITIES_CALLS));

  const { firstName, lastName } = callUser || {};
  const userFullName = getPersonFullName({ firstName, lastName }) ?? '';

  const callDispositionAutocompleteValue = callOutcomeOptions.find(
    item => item.value === callDisposition,
  );

  const headerDate = formatActivityDateTime(dateTime.concat('+00:00'));

  const { fullNameFrom, fullNameTo } = useMemo(() => {
    const isCallInbound = !!employeeUserId && callType === CallTypes.INBOUND;

    const fullNameTo = isCallInbound ? userFullName : getFullNameByNumber(contacts, phoneNumberTo);

    //potentially there can be other call types apart from the outbound call
    const fullNameFrom = !isCallInbound
      ? userFullName
      : getFullNameByNumber(contacts, phoneNumberFrom);

    return { fullNameFrom, fullNameTo };
  }, [callType, employeeUserId, phoneNumberFrom, phoneNumberTo, contacts, userFullName]);

  const onCallOutcomeChange = useCallback(
    (__, option) => {
      dispatch(updateCallOutcomeAction({ callHistoryId, callDisposition: option.value }))
        .then(() => {
          const message = `Call outcome has been successfully updated`;

          dispatch(showMessageAction({ message, variant: 'success' }));
        })
        .catch(Error);
    },
    [callHistoryId, dispatch],
  );

  return (
    <Accordion
      bottom={
        <>
          <Divider />

          <div className="flex justify-between">
            <div className="flex p-10">
              {isCompanyUsersLoading ? (
                <Skeleton animation="pulse" className="h-20 w-200" variant="text" />
              ) : (
                <>
                  <Typography className="mr-32" color="inherit" variant="regular14">
                    To:
                  </Typography>
                  <Typography className="mr-12" color="inherit" variant="medium14">
                    <HighlightText highlight={highlightText} text={fullNameTo || ''} />
                  </Typography>
                  <Typography color="inherit" variant="medium14">
                    <ClickablePhone
                      applicationId={applicationId}
                      typographyVariant="inherit"
                      value={phoneNumberTo}
                    />
                  </Typography>
                </>
              )}
            </div>
            <div className="flex items-center px-10">
              <Typography className="mr-32" color="inherit" variant="regular14">
                Call outcome:
              </Typography>
              <AutocompleteCDS
                disableClearable
                data-field-id={MarkedAppFormsTreeFieldId.ACTIVITIES_CALL_OUTCOME}
                dataTestId={ActivitiesTestIds.getCallOutcomeSelectId(callHistoryId)}
                disabled={isPageLocked || lockedFieldsMap.callOutcome}
                id={`callOutcome-${callHistoryId}`}
                isHidden={hiddenFieldsMap.callOutcome || isPageHidden}
                name="callOutcome"
                options={callOutcomeOptions}
                value={callDispositionAutocompleteValue}
                width={200}
                onChange={onCallOutcomeChange}
              />
            </div>
          </div>

          {recordingUrl && (
            <>
              <Divider />

              <AudioPlayer link={recordingUrl} />
            </>
          )}

          <Divider />

          <CallNotes callHistoryId={callHistoryId} callNotes={callNotes} />

          {!!callTranscription && (
            <>
              <Divider />

              <CallTranscription id={callHistoryId} transcription={callTranscription} />
            </>
          )}
        </>
      }
      className="overflow-hidden mb-8"
      defaultExpanded={false}
      id={`call-${callHistoryId}`}
      isWithDivider={false}
      isWithExpandIcon={false}
      sx={classes.accordion}
      top={
        <Paper className="w-full flex justify-between items-center shadow-none rounded-12 px-20 py-12">
          <div className="flex items-center">
            <PhoneIcon className="mr-20" size={36} />

            {isCompanyUsersLoading ? (
              <Skeleton animation="pulse" className="h-20 w-360" variant="text" />
            ) : (
              <>
                <Typography className="mr-16" color="inherit" variant="medium16">
                  Call
                </Typography>
                <Typography color="inherit" variant="medium14">
                  from <HighlightText highlight={highlightText} text={fullNameFrom || ''} />{' '}
                  <HighlightText
                    highlight={highlightText}
                    text={normalizeNumber(phoneNumberFrom)}
                  />
                </Typography>
              </>
            )}
          </div>
          <div className="flex flex-col items-end">
            {isCompanyUsersLoading ? (
              <Skeleton animation="pulse" className="h-20" variant="text" width="100%" />
            ) : (
              <Typography noWrap color="textSecondary" variant="regular14">
                to <HighlightText highlight={highlightText} text={fullNameTo || phoneNumberTo} />
              </Typography>
            )}

            <Typography variant="regular14">{headerDate}</Typography>
          </div>
        </Paper>
      }
    />
  );
};

CallActivityItem.propTypes = {
  isCompanyUsersLoading: PropType.bool.isRequired,
  activity: PropType.object,
};

export default memo(CallActivityItem);
